import clsx from 'clsx'
import { type ChangeEvent, type FC, type RefObject } from 'react'
import { useThemeMode } from '../../../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import { type BackgroundType } from '../types'
import { VideoCallIcon } from './videoCallIcons'

type VirtualBackgroundModalProps = {
  customBackgrounds: string[]
  previewBackground: string | null
  selectedBackgroundUrl: string | null
  fileInputRef: RefObject<HTMLInputElement>
  onClose: () => void
  onClearPreview: () => void
  onFileUpload: (event: ChangeEvent<HTMLInputElement>) => void
  onApplyPreview: () => void
  onApplyBackground: (type: BackgroundType, imageUrl?: string) => void
  onRemoveBackground: (index: number) => void
}

export const VirtualBackgroundModal: FC<VirtualBackgroundModalProps> = ({
  customBackgrounds,
  previewBackground,
  selectedBackgroundUrl,
  fileInputRef,
  onClose,
  onClearPreview,
  onFileUpload,
  onApplyPreview,
  onApplyBackground,
  onRemoveBackground,
}) => {
  const { mode } = useThemeMode()

  return (
    <div
      className={clsx(
        'modal fade show d-block',
        mode === 'dark' ? 'bg-gray-800 bg-opacity-25' : 'bg-gray-900 bg-opacity-75'
      )}
      role='dialog'
      tabIndex={-1}
      onClick={onClose}
    >
      <div
        className='modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable'
        onClick={(event) => event.stopPropagation()}
      >
        <div className='modal-content'>
          <div className='modal-header'>
            <h3 className='modal-title fw-semibold text-gray-900 fs-4'>Fundos Personalizados</h3>
            <button
              className='btn btn-sm btn-icon btn-active-light-primary'
              onClick={onClose}
              type='button'
            >
              <i className={`${VideoCallIcon.Close} fs-4 text-gray-500`} />
            </button>
          </div>

          <div className='modal-body'>
            {previewBackground && (
              <div className='rounded p-4 mb-6 bg-light border border-2 border-primary'>
                <div className='d-flex align-items-center justify-content-between mb-3'>
                  <span className='fw-semibold text-primary'>
                    <i className={`${VideoCallIcon.Preview} me-2`} />
                    Preview do Novo Fundo
                  </span>
                  <button
                    onClick={onClearPreview}
                    className='btn btn-sm btn-icon btn-active-light-primary'
                    type='button'
                  >
                    <i className={`${VideoCallIcon.Close} text-gray-500`} />
                  </button>
                </div>
                <img
                  className='rounded mb-3 w-100 h-200px object-fit-cover'
                  src={previewBackground}
                  alt='Preview do novo fundo'
                />
                <button
                  onClick={onApplyPreview}
                  className='btn btn-primary w-100 fw-semibold'
                  type='button'
                >
                  <i className={`${VideoCallIcon.Check} me-2`} />
                  Aplicar este fundo
                </button>
              </div>
            )}

            <button
              onClick={() => fileInputRef.current?.click()}
              className='btn btn-dashed btn-flex w-100 py-4 mb-6 notice d-flex flex-center rounded border border-2 border-dashed border-gray-800 bg-gray-100'
              type='button'
            >
              <i className={`${VideoCallIcon.Upload} fs-2 text-primary me-3`} />
              <span className='fw-semibold text-gray-900'>Fazer upload de imagem</span>
            </button>
            <input
              ref={fileInputRef}
              type='file'
              accept='image/jpeg,image/png,image/webp'
              onChange={onFileUpload}
              className='d-none'
            />

            <div>
              <h4 className='mb-4 text-gray-900 fs-6 fw-semibold'>
                Fundos Salvos ({customBackgrounds.length})
              </h4>
              {customBackgrounds.length === 0 ? (
                <div className='rounded p-10 text-center bg-light'>
                  <i className={`${VideoCallIcon.Images} fs-3x mb-4 text-gray-400`} />
                  <p className='mb-0 text-gray-800'>Nenhum fundo personalizado salvo ainda.</p>
                  <p className='mt-2 mb-0 fs-7 text-gray-600'>
                    Faça upload de uma imagem para começar.
                  </p>
                </div>
              ) : (
                <div className='row g-4'>
                  {customBackgrounds.map((background, index) => (
                    <div key={index} className='col-6 col-md-4'>
                      <div
                        className={clsx(
                          'ratio ratio-16x9 position-relative overflow-hidden rounded border border-2',
                          selectedBackgroundUrl === background
                            ? 'border-primary shadow'
                            : 'border-gray-300'
                        )}
                      >
                        <img
                          src={background}
                          alt={`Fundo personalizado ${index + 1}`}
                          className='w-100 h-100 object-fit-cover cursor-pointer'
                          onClick={() => onApplyBackground('image', background)}
                        />
                        <button
                          onClick={(event) => {
                            event.stopPropagation()
                            if (window.confirm('Deseja remover este fundo?')) {
                              onRemoveBackground(index)
                            }
                          }}
                          className='btn btn-sm btn-icon btn-light-danger position-absolute top-0 end-0 m-2 rounded-circle'
                          type='button'
                          title='Remover fundo'
                        >
                          <i className={VideoCallIcon.Trash} />
                        </button>
                        {selectedBackgroundUrl === background && (
                          <div className='badge badge-primary position-absolute bottom-0 end-0 m-2'>
                            <i className={`${VideoCallIcon.Check} me-1`} />
                            Ativo
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className='modal-footer'>
            <button onClick={onClose} className='btn btn-secondary fw-semibold' type='button'>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
